import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useToast } from '@/shared/ui/Toast';
import { agencyApi, type Location } from '../../api/agencyApi';
import { agencyKeys } from '../../queryKeys';
import styles from './ModuleControls.module.css';

type Kind = 'campaign' | 'content';

const COPY: Record<Kind, { label: string; desc: string; ariaLabel: string }> = {
  campaign: {
    label: 'Campaign approvals',
    desc: 'Autopilot launches recommended campaigns without waiting. Guided holds each one for the practice to approve first.',
    ariaLabel: 'Ads autopilot',
  },
  content: {
    label: 'Content approvals',
    desc: 'Autopilot publishes generated content on schedule. Guided queues each piece for review before it goes out.',
    ariaLabel: 'Content autopilot',
  },
};

/** Both toggles control a dashboard feature only — never what the agent itself does. */
const AGENT_NOTE = 'This controls a dashboard feature here, not what the agent does.';

/**
 * Guided / Autopilot switch for one location's campaign or content approvals.
 * Ported from the Setup tab's location row: same `updateLocation` call, same
 * stored values (`autopilot`/`guided` for campaigns, `auto`/`review` for content).
 *
 * Unlike content, the backend never defaults a null campaign (Ads) preference to autopilot (see
 * campaignRecs.ts's `=== 'autopilot'` check) — so campaign reads the raw value and shows "Not set"
 * rather than defaulting the display to a mode the backend isn't actually running.
 */
export function ApprovalPreferenceToggle({
  practiceId,
  location,
  kind,
}: {
  practiceId: string;
  location: Location;
  kind: Kind;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const rawPref =
    kind === 'campaign'
      ? location.campaignApprovalPreference
      : (location.contentApprovalPreference ?? 'auto');
  const isAutopilot = kind === 'campaign' ? rawPref === 'autopilot' : rawPref === 'auto';
  const isNotSet = kind === 'campaign' && rawPref == null;

  const prefMutation = useMutation({
    mutationFn: (updates: Partial<Location>) =>
      agencyApi.updateLocation(practiceId, location.id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      toast.success('Preference updated.');
    },
    onError: () => toast.error('Failed to update preference.'),
  });

  const flip = () => {
    if (kind === 'campaign') {
      prefMutation.mutate({ campaignApprovalPreference: isAutopilot ? 'guided' : 'autopilot' });
    } else {
      prefMutation.mutate({ contentApprovalPreference: isAutopilot ? 'review' : 'auto' });
    }
  };

  const copy = COPY[kind];
  const stateLabel = isNotSet ? 'Not set' : isAutopilot ? 'Autopilot' : 'Guided';

  return (
    <div className={styles.row}>
      <div>
        <div className={styles.label}>{copy.label}</div>
        <div className={styles.desc}>{copy.desc}</div>
        <p className={styles.note}>{AGENT_NOTE}</p>
      </div>
      <div className={styles.control}>
        <button
          type="button"
          role="switch"
          aria-checked={isAutopilot}
          aria-label={copy.ariaLabel}
          disabled={prefMutation.isPending}
          onClick={flip}
          className={clsx(styles.toggle, isAutopilot && styles.toggleOn)}
        >
          <span className={styles.knob} />
        </button>
        <span className={styles.state}>{stateLabel}</span>
      </div>
    </div>
  );
}
